import useUrlState from '@ahooksjs/use-url-state'
import { DownOutlined } from '@ant-design/icons'
import { useAntdTable, useRequest, useTitle } from 'ahooks'
import { Dropdown, Menu, Modal, Spin, Tabs } from 'antd'
import { useAtom } from 'jotai'
import { assign, merge, omit } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { RouteChildrenProps, useHistory } from 'react-router-dom'

import {
  LockIcon,
  MosaicComponent,
  NoPermissionPage,
  PERMISSIONS,
  getPointPermission,
  hasPointPagePermission,
} from '@/components'
import { PpFileContainer } from '@/components/files'
import { ReportContainer } from '@/components/reports/report.container'
import { ResourceAudit } from '@/components/resource-audit'
import { BigProjectResourceDashboard } from '@/components/user-resource-dashboard/big-project/big-project-resource-dashboard'
import { SpecialProjectWorkResourceContainer } from '@/components/work-resource/special-project/special-project-work-resource.container'
import { Header } from '@/modules/header'
import { NormalResourceTable, deleteChildren } from '@/modules/portfolio/portfolio-list'
import { userInfoAtom } from '@/modules/user'
import { FetchType, PAGE_RESOURCE_POINTS_ENUM, PpRelation, ResourceType, ResourceUser } from '@/schemas'
import { ga, traceEvent } from '@/utils/ga'

import { Devlop, DevlopStatus } from '../devlop.schema'
import { DevlopHeaderContainer } from './devlop-header/devlop-header.container'
import { DevlopJQLContainer } from './devlop-jql/devlop-jql.container'
import { DevlopSummaryContainer } from './devlop-summary'

const { TabPane } = Tabs

export const DevlopDetailContainer: React.FC<RouteChildrenProps<{ selfId: string }>> = ({ match }) => {
  const { t } = useTranslation()
  const selfId = match!.params.selfId
  const reportRef = React.useRef<{ entryTime: number; name?: string; entry?: string; users?: ResourceUser[] }>({
    entryTime: +new Date(),
    users: [],
  })

  const [visible, setVisible] = React.useState(false)

  const ref = React.useRef<{ workResourceRefresh?: () => void; auditRefresh?: () => void }>()

  const { tableProps } = useAntdTable<PpRelation[], any, PpRelation[]>(
    {
      url: '/api/relations/getPpRelations',
      params: {
        parentSelfId: selfId,
        rootTypes: [ResourceType.PROJECT, ResourceType.PORTFOLIO],
        childTypes: [ResourceType.PROJECT, ResourceType.PORTFOLIO],
        fetchType: FetchType.fetchChild,
      },
    },
    {
      refreshDeps: [selfId],
      formatResult: (data) => ({ list: deleteChildren(data), total: 0 }),
    }
  )

  const { data: devlop, refresh } = useRequest<Devlop>(
    {
      url: `/api/devlop/detail/${selfId}`,
    },
    {
      onError() {
        history.push('/devlop')
      },
    }
  )

  const { run: updateDevlop, loading: updateLoading } = useRequest(
    (data) => ({
      url: `/api/devlop/patch/${selfId}`,
      method: 'patch',
      data,
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
        setVisible(false)
      },
      onError() {
        setVisible(false)
      },
    }
  )

  useTitle(devlop ? devlop.name : document.title, { restoreOnUnmount: true })

  const [users, setUsers] = React.useState<ResourceUser[]>()
  React.useEffect(() => {
    if (users) {
      // reportRef.current = assign(reportRef.current, { users })
    }
  }, [users])
  const [viewer] = useAtom(userInfoAtom)

  const reportExposeTime = () => {
    const report = reportRef.current
    if (report.name && report.entry && report.entryTime) {
      ga('event', traceEvent.detailPage.events.expose_time_devlop_detail, {
        event_label: report.name,
        event_value: Math.round((+new Date() - report.entryTime) / 1000),
        event_category: report.entry,
        action: traceEvent.detailPage.action,
        target: traceEvent.detailPage.targets.ef_pmo_devlop_detail,
        payload: report.users
          ? report.users.some((user: ResourceUser) => user.email === viewer.email)
            ? '1'
            : '0'
          : '',
      })
    }
  }

  React.useEffect(() => {
    const entry = window.sessionStorage.getItem('entry')
    reportRef.current = assign(reportRef.current, { entry: entry || 'ef_pmo_devlops' })
    sessionStorage.removeItem('entry')

    window.addEventListener('beforeunload', reportExposeTime)
    return () => {
      window.removeEventListener('beforeunload', reportExposeTime)
      reportExposeTime()
    }
  }, [])

  React.useEffect(() => {
    if (devlop) {
      reportRef.current = assign(reportRef.current, { name: devlop.name })
    }
  }, [devlop])

  const history = useHistory()
  const [state, setState] = useUrlState(
    { tab: new URLSearchParams(window.location.search).get('tab') || 'detail' },
    {
      navigateMode: 'replace',
    }
  )

  if (!devlop) {
    return (
      <div className='flex h-full justify-center items-center'>
        <Spin size='large' delay={300} />
      </div>
    )
  }
  const { canUpdate } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况, devlop.permissions)
  const isFinished = [DevlopStatus.已结项].includes(devlop.status)
  const ChangeStatusMenu = (
    <Menu>
      <Menu.Item
        key={'pause'}
        disabled={devlop.status === DevlopStatus.暂停}
        onClick={() => updateDevlop({ status: DevlopStatus.暂停 })}
      >
        {t('devlop.status.pause')}
      </Menu.Item>
      <Menu.Item
        key={'ongoing'}
        disabled={devlop.status === DevlopStatus.进行中}
        onClick={() => updateDevlop({ status: DevlopStatus.进行中 })}
      >
        {t('devlop.status.ongoing')}
      </Menu.Item>
      {/* <Menu.Item
        key={'termination'}
        disabled={devlop.status === DevlopStatus.终止}
        onClick={() => updateDevlop({ status: DevlopStatus.终止 })}
      >
        {t('devlop.status.termination')}
      </Menu.Item> */}
      <Menu.Item
        key={'notStart'}
        disabled={devlop.status === DevlopStatus.未开始}
        onClick={() => updateDevlop({ status: DevlopStatus.未开始 })}
      >
        {t('devlop.status.notStart')}
      </Menu.Item>
      <Menu.Item disabled={!canUpdate || isFinished} onClick={() => setVisible(true)} key={DevlopStatus.已结项}>
        {devlop.status === DevlopStatus.已结项 ? t('已结项') : t('结项')}
      </Menu.Item>
    </Menu>
  )

  const translateMap = {
    [DevlopStatus.未开始]: t('devlop.status.notStart'),
    [DevlopStatus.进行中]: t('devlop.status.ongoing'),
    [DevlopStatus.暂停]: t('devlop.status.pause'),
    // [DevlopStatus.终止]: t('devlop.status.termination'),
    [DevlopStatus.已结项]: t('devlop.status.finished.title'),
  }

  const onTabTrace = (tab: string, devlopName: string) => {
    const devlopDetail = traceEvent.devlopDetail
    switch (tab) {
      case 'detail':
        ga('event', devlopDetail.events.click_switch_tab, {
          event_category: devlopDetail.event_categories.ef_pmo_devlops_detail,
          action: devlopDetail.action,
          event_label: devlopName,
          target: devlopDetail.targets.detail,
        })
        break
      case 'report':
        ga('event', devlopDetail.events.click_switch_tab, {
          event_category: devlopDetail.event_categories.ef_pmo_devlops_detail,
          action: devlopDetail.action,
          event_label: devlopName,
          target: devlopDetail.targets.weekly_reports,
        })
        break
    }
  }

  return (
    <>
      <Header
        title={
          <div className='flex justify-center items-center'>
            <DevlopHeaderContainer
              project={devlop}
              refreshProject={() => {
                refresh()
                ref.current?.workResourceRefresh?.()
                ref.current?.auditRefresh?.()
              }}
            />

            <div className='cursor-pointer h-4 text-sm px-4'>
              {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_状态, devlop.permissions).canRead ? (
                [DevlopStatus.暂停, DevlopStatus.进行中, DevlopStatus.未开始].includes(devlop?.status) ? (
                  <Dropdown trigger={['click']} overlay={ChangeStatusMenu} placement='bottom'>
                    <span>
                      {translateMap[devlop.status]}
                      <DownOutlined className='pl-1' />
                    </span>
                  </Dropdown>
                ) : (
                  <div className='h-4 text-sm px-4'>{translateMap[devlop.status]}</div>
                )
              ) : (
                <MosaicComponent />
              )}
            </div>
          </div>
        }
        destroyInactiveTabPane
      />
      <Tabs
        destroyInactiveTabPane
        activeKey={state.tab}
        onChange={(tab) => {
          const keys = Object.keys(state)
          setState({
            ...merge({}, ...keys.map((key) => ({ [key]: undefined }))), // reset url params
            tab,
          })
          onTabTrace(tab, devlop.name)
          if (tab === 'detail') refresh()
        }}
      >
        <TabPane
          tab={
            <span>
              {t('overview')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.概况, PERMISSIONS.READ, devlop.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='detail'
        >
          {state.tab === 'detail' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.概况, PERMISSIONS.READ, devlop.permissions) ? (
              <DevlopSummaryContainer devlop={devlop} setUsers={setUsers} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.概况} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('requirement problem list')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.需求与问题, PERMISSIONS.READ, devlop.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='list'
        >
          {state.tab === 'list' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.需求与问题, PERMISSIONS.READ, devlop.permissions) ? (
              <DevlopJQLContainer devlop={devlop} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.需求与问题} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('resource dashboard')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源看板, PERMISSIONS.READ, devlop.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='dashboard'
        >
          {state.tab === 'dashboard' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源看板, PERMISSIONS.READ, devlop.permissions) ? (
              <BigProjectResourceDashboard
                selfId={devlop.selfId}
                resourceType={ResourceType.DEVLOP}
                resource={devlop}
              />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.资源看板} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('devlop.resource')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, PERMISSIONS.READ, devlop.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='userResource'
        >
          {state.tab === 'userResource' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, PERMISSIONS.READ, devlop.permissions) ? (
              <SpecialProjectWorkResourceContainer ref={ref} resource={devlop} resourceType={ResourceType.DEVLOP} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.资源管理} />
            ))}
        </TabPane>
        <TabPane
          tab={
            <span>
              {t('reports')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.周报, PERMISSIONS.READ, devlop.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='report'
        >
          {state.tab === 'report' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.周报, PERMISSIONS.READ, devlop.permissions) ? (
              <ReportContainer
                selfId={devlop.selfId}
                type={devlop.type}
                permissions={devlop.permissions}
                name={devlop.name}
                refreshProject={refresh}
              />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.周报} />
            ))}
        </TabPane>

        <TabPane
          tab={
            <span>
              {t('file')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.文件, PERMISSIONS.READ, devlop.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='file'
        >
          {state.tab === 'file' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.文件, PERMISSIONS.READ, devlop.permissions) ? (
              <PpFileContainer pp={devlop} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.文件} />
            ))}
        </TabPane>

        <TabPane
          tab={
            <span>
              {t('audit.title')}
              {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.审批, PERMISSIONS.READ, devlop.permissions) && (
                <LockIcon />
              )}
            </span>
          }
          key='audit'
        >
          {state.tab === 'audit' &&
            (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.审批, PERMISSIONS.READ, devlop.permissions) ? (
              <ResourceAudit ref={ref} selfId={devlop.selfId} type={devlop.type} />
            ) : (
              <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.审批} />
            ))}
        </TabPane>

        {/* {[DevlopStatus.已结项].includes(devlop.status) ? (
          <TabPane
            tab={
              <span>
                {t('devlop.evaluation')}
                {!hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.结项, PERMISSIONS.READ, devlop.permissions) && (
                  <LockIcon />
                )}
              </span>
            }
            key='finish'
          >
            {state.tab === 'finish' &&
              (hasPointPagePermission(PAGE_RESOURCE_POINTS_ENUM.结项, PERMISSIONS.READ, devlop.permissions) ? (
                <ResourceEvaluationContainer onRefresh={refresh} resource={devlop} />
              ) : (
                <NoPermissionPage resourcePoint={PAGE_RESOURCE_POINTS_ENUM.结项} />
              ))}
          </TabPane>
        ) : null} */}
      </Tabs>
      <Modal
        width={1000}
        title={t('结项')}
        okText={t('customer.flowStatus.ProjectWaitForFinish.popover.button')}
        okButtonProps={{ loading: updateLoading }}
        cancelButtonProps={{ disabled: updateLoading }}
        onOk={() => {
          updateDevlop({ status: DevlopStatus.已结项 })
        }}
        onCancel={() => {
          setVisible(false)
        }}
        visible={visible}
      >
        <div className='text-red-500'>
          <div>{t('project.status.close.tooltip')}</div>
          <div>{t('project.status.close.tooltip2')}</div>
        </div>
        <div className='max-h-[500px] overflow-y-auto'>
          <NormalResourceTable
            selfId={devlop.selfId}
            type={ResourceType.DEVLOP}
            pageType={ResourceType.DEVLOP}
            name={document.title}
            onSuccess={refresh}
            canDelete={false}
            canUpdate={false}
            {...omit(tableProps, 'pagination')}
            sticky={true}
          />
        </div>
      </Modal>
    </>
  )
}
